import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap } from 'rxjs/operators';

import { EquipmentApi, ServiceDocumentsApi, WebsocketSmartNoteStatus } from '@vi-iot/client';

import { InstallationDetailsStore } from '@store';

import { ActiveErrorsActions } from './active-errors.actions';
import { ActiveErrorsMappers } from './active-errors.mappers';
import { ACTIVE_ERROR_TYPE, ActiveErrorData } from './active-errors.model';

@Injectable()
export class ActiveErrorsEffects {
  readActiveErrors$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ActiveErrorsActions.readActiveErrors.attempt),
      concatLatestFrom(() =>
        this.installationDetailsStore.installationGateways$.pipe(filter((device) => !!device))
      ),
      switchMap(([action, installationGateways]) =>
        this.equipmentApi.listActiveError(action.installationId).pipe(
          map((activeErrorsResponse) => {
            const activeErrorsFilteredResponse = activeErrorsResponse.data.filter(
              (entry) =>
                entry.type === ACTIVE_ERROR_TYPE.DEVICE_ERROR ||
                entry.type === ACTIVE_ERROR_TYPE.SYSTEM_LOCKED ||
                entry.type === ACTIVE_ERROR_TYPE.FILTER_EXCHANGE ||
                entry.type === ACTIVE_ERROR_TYPE.FILTER_EXCHANGE_14_DAYS ||
                entry.type === ACTIVE_ERROR_TYPE.SALT_CARTRIDGE_EXCHANGE ||
                entry.type === ACTIVE_ERROR_TYPE.MAINTENANCE_TIMEBASED ||
                entry.type === ACTIVE_ERROR_TYPE.MAINTENANCE_BURNERBASED ||
                entry.type === ACTIVE_ERROR_TYPE.MAINTENANCE_NEEDED ||
                entry.type === ACTIVE_ERROR_TYPE.MAINTENANCE_NEEDED_IN_30_D ||
                entry.type === ACTIVE_ERROR_TYPE.MAINTENANCE_NEEDED_IN_45_D ||
                entry.type === ACTIVE_ERROR_TYPE.DEVICE_BATTERY_LEVEL_LOW ||
                entry.type === ACTIVE_ERROR_TYPE.FILTER_CHANGE_MODE_ACTIVATED ||
                entry.type === ACTIVE_ERROR_TYPE.FLOOR_HEATING_OVERHEATING ||
                entry.type === ACTIVE_ERROR_TYPE.POWER_LIMITATION_CONSUMPTION ||
                entry.type === ACTIVE_ERROR_TYPE.POWER_LIMITATION_POWER_PRODUCTION
            );

            const activeErrors: ActiveErrorData[] = this.activeErrorsMappers.mapResponseIntoStoreModel(
              activeErrorsFilteredResponse,
              installationGateways
            );

            return ActiveErrorsActions.readActiveErrors.success({
              activeErrors,
            });
          }),
          catchError((error) => of(ActiveErrorsActions.readActiveErrors.error({ error })))
        )
      )
    );
  });

  liveUpdateActiveError$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ActiveErrorsActions.liveUpdateActiveError.attempt),
      concatLatestFrom(() =>
        this.installationDetailsStore.installationGateways$.pipe(filter((device) => !!device))
      ),
      map(([action, installationGateways]) => {
        if (action.smartNote.action === WebsocketSmartNoteStatus.Created) {
          const activeErrors: ActiveErrorData[] = this.activeErrorsMappers.mapResponseIntoStoreModel(
            [action.smartNote.note],
            installationGateways
          );

          return ActiveErrorsActions.liveUpdateActiveError.new({ errorData: activeErrors[0] });
        } else if (action.smartNote.action === WebsocketSmartNoteStatus.Deleted) {
          return ActiveErrorsActions.liveUpdateActiveError.delete({
            smartNote: action.smartNote.note,
          });
        }
      })
    );
  });

  readActiveErrorsSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ActiveErrorsActions.readActiveErrors.success),
        map((action) => {
          action.activeErrors.forEach((error) =>
            this.store.dispatch(
              ActiveErrorsActions.readActiveErrorDetails.attempt({
                deviceSerial: error.deviceSerial,
                errorCode: error.errorCode,
                countryCode: error.countryCode,
                langCode: error.langCode,
              })
            )
          );
        })
      );
    },
    { dispatch: false }
  );

  getErrorDetails$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ActiveErrorsActions.readActiveErrorDetails.attempt),
      mergeMap((action) => {
        const shortDeviceSerial = (action.deviceSerial || '').slice(0, 7);
        if (shortDeviceSerial.length === 7 && action.errorCode) {
          return this.serviceDocumentsApi
            .listErrorsDatabase({
              materialNumber: shortDeviceSerial,
              errorCode: action.errorCode,
              countryCode: action.countryCode,
              languageCode: action.langCode,
            })
            .pipe(
              map((errorsData) => {
                return ActiveErrorsActions.readActiveErrorDetails.success({
                  deviceSerial: action.deviceSerial,
                  errorDetails: errorsData,
                  errorCode: action.errorCode,
                  langCode: action.langCode,
                });
              }),
              catchError((error) =>
                of(
                  ActiveErrorsActions.readActiveErrorDetails.error({
                    error,
                    deviceSerial: action.deviceSerial,
                    errorCode: action.errorCode,
                  })
                )
              )
            );
        } else {
          return of(
            ActiveErrorsActions.readActiveErrorDetails.errorAborted({
              deviceSerial: action.deviceSerial,
              errorCode: action.errorCode,
            })
          );
        }
      })
    );
  });

  constructor(
    private actions$: Actions,
    private store: Store,
    private equipmentApi: EquipmentApi,
    private serviceDocumentsApi: ServiceDocumentsApi,
    private activeErrorsMappers: ActiveErrorsMappers,
    private installationDetailsStore: InstallationDetailsStore
  ) {}
}
